import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { PusherContext } from '../pages/PusherContext'
import {
  wsGoalToTimelineEvent,
  wsShotToTimelineEvent,
  wsPenaltyToTimelineEvent,
  wsGoalieChangeToTimelineEvent,
  createInitialWsTimelineState,
  createInitialWsGameState,
} from '@sportninja/common/utils/wsTimelineTransformer'
import { mapStatusToBroadcastState } from '@sportninja/common/utils/mapStatusToBroadcastState'

/**
 * Valid stream types for multi-stream support
 * @type {Array<string>}
 */
const VALID_STREAM_TYPES = ['league', 'home', 'visitor']

/**
 * Create initial state for a single stream type
 * @param {string} type - Stream type ('league' | 'home' | 'visitor')
 * @returns {Object} Initial stream state
 */
const createInitialStreamState = (type) => ({
  type,
  livestreamId: null,
  broadcastState: 'idle',
  streamUrl: null,
  broadcastId: null,
  isLive: false,
  wasLive: false,
  hasRecording: false,
  recordingUrl: null,
})

// mapStatusToBroadcastState is now imported from @sportninja/common/utils/mapStatusToBroadcastState

/**
 * Hook for subscribing to game livestream updates via Pusher WebSocket
 * Provides real-time broadcast state updates for live streaming
 * Supports multiple simultaneous streams (league, home, visitor)
 *
 * @param {string|null} gameId - The game ID to subscribe to
 * @returns {Object} Broadcast state and controls
 */
const useGameLivestream = (gameId, initialLivestreams) => {
  const { client, isReady } = useContext(PusherContext) || {}

  // Multi-stream state
  const [streams, setStreams] = useState({
    league: createInitialStreamState('league'),
    home: createInitialStreamState('home'),
    visitor: createInitialStreamState('visitor'),
  })

  // Stream selection state
  const [selectedStreamType, setSelectedStreamType] = useState(null)

  // Connection and error state
  const [connectionState, setConnectionState] = useState('disconnected')
  const [error, setError] = useState(null)
  const [gameInfo, setGameInfo] = useState({
    homeTeamName: null,
    visitingTeamName: null,
    scheduleName: null,
  })

  // WebSocket score state (prioritized over API scores)
  const [wsHomeScore, setWsHomeScore] = useState(null)
  const [wsVisitingScore, setWsVisitingScore] = useState(null)
  const [lastScoreUpdate, setLastScoreUpdate] = useState(null)

  // WebSocket timeline events overlay (until next API refresh)
  const [wsTimelineEvents, setWsTimelineEvents] = useState(
    createInitialWsTimelineState
  )

  // WebSocket game state (period/clock info)
  const [wsGameState, setWsGameState] = useState(createInitialWsGameState)

  // Cheers state
  const [cheerEvents, setCheerEvents] = useState([])

  // Refs
  const channel = useRef(null)
  const reactionsChannel = useRef(null)
  const wasLiveRefs = useRef({
    league: false,
    home: false,
    visitor: false,
  })

  // Computed values
  const activeStreams = useMemo(
    () => Object.values(streams).filter((s) => s.isLive),
    [streams]
  )
  const activeStreamCount = activeStreams.length

  // Available streams (live OR recorded)
  const availableStreams = useMemo(() => {
    const allStreams = Object.values(streams)
    return allStreams.filter((s) => s.isLive || s.hasRecording)
  }, [streams])
  const availableStreamCount = availableStreams.length

  const selectedStream = useMemo(
    () => (selectedStreamType ? streams[selectedStreamType] : null),
    [selectedStreamType, streams]
  )

  // Computed broadcastState for backward compatibility
  const broadcastState = useMemo(() => {
    if (activeStreamCount > 0) {
      return 'live'
    }
    const anyWasLive = Object.values(streams).some((s) => s.wasLive)
    return anyWasLive ? 'ended' : 'idle'
  }, [activeStreamCount, streams])

  // Legacy compatibility: first active or selected stream URL
  const streamUrl = useMemo(
    () => selectedStream?.streamUrl || activeStreams[0]?.streamUrl || null,
    [selectedStream, activeStreams]
  )

  const broadcastId = useMemo(
    () => selectedStream?.broadcastId || activeStreams[0]?.broadcastId || null,
    [selectedStream, activeStreams]
  )

  const isLive = activeStreamCount > 0

  // Subscribe to game livestream channel (PUBLIC channel like mobile)
  useEffect(() => {
    if (!client || !isReady || !gameId) {
      return
    }

    const channelName = `game.${gameId}`
    const reactionsChannelName = `game.${gameId}.reactions`

    try {
      channel.current = client.subscribe(channelName)

      channel.current.bind('pusher:subscription_succeeded', () => {
        setConnectionState('connected')
        setError(null)
      })

      channel.current.bind('pusher:subscription_error', (err) => {
        console.error(
          `[useGameLivestream] Subscription error for ${channelName}:`,
          err
        )
        setConnectionState('error')
        setError('Failed to subscribe to game updates')
      })

      // Listen for 'livestream.status' event (the broadcastAs() name from Laravel)
      channel.current.bind('livestream.status', (data) => {
        const {
          livestream_type: streamType = 'league',
          livestream_id: livestreamId,
          status: backendStatus,
          is_broadcasting: newIsBroadcasting,
          playback_url: newPlaybackUrl,
          broadcast_id: newBroadcastId,
          home_team_name: homeTeamName,
          visiting_team_name: visitingTeamName,
          schedule_name: scheduleName,
        } = data

        // Validate stream type
        const validStreamType = VALID_STREAM_TYPES.includes(streamType)
          ? streamType
          : 'league'

        // Disconnected = encoder dropped but reconnect window is open.
        // Do nothing — keep current state, video keeps playing its buffer.
        if (backendStatus === 'disconnected') {
          wasLiveRefs.current[validStreamType] = true
          return
        }

        const newIsLive =
          newIsBroadcasting ||
          backendStatus === 'active' ||
          backendStatus === 'connected' ||
          backendStatus === 'recording'

        if (newIsLive) {
          wasLiveRefs.current[validStreamType] = true
        }

        const isRecordingReady = backendStatus === 'recording_ready'
        const newStreamState = mapStatusToBroadcastState(
          backendStatus,
          wasLiveRefs.current[validStreamType],
          newBroadcastId,
          newPlaybackUrl
        )

        setStreams((prev) => ({
          ...prev,
          [validStreamType]: {
            type: validStreamType,
            livestreamId: livestreamId || null,
            broadcastState: newStreamState,
            streamUrl: newPlaybackUrl || null,
            broadcastId: newBroadcastId || null,
            isLive: newIsLive,
            wasLive: wasLiveRefs.current[validStreamType] || newIsLive,
            hasRecording:
              isRecordingReady || prev[validStreamType]?.hasRecording || false,
            recordingUrl: isRecordingReady
              ? newPlaybackUrl
              : prev[validStreamType]?.recordingUrl || null,
          },
        }))

        // Update game info
        setGameInfo({
          homeTeamName: homeTeamName || null,
          visitingTeamName: visitingTeamName || null,
          scheduleName: scheduleName || null,
        })
      })

      // Listen for 'score.updated' event for real-time score updates
      channel.current.bind('score.updated', (data) => {
        const {
          home_team_score: homeScore,
          visiting_team_score: visitingScore,
        } = data

        setWsHomeScore(homeScore)
        setWsVisitingScore(visitingScore)
        // Update game state with period/clock info from score event
        if (data.period_clock_time || data.period_number) {
          setWsGameState((prev) => ({
            ...prev,
            periodNumber: data.period_number ?? prev.periodNumber,
            periodName: data.period_name ?? prev.periodName,
            clockTime: data.period_clock_time ?? prev.clockTime,
            isOvertime: data.is_overtime ?? prev.isOvertime,
            isShootout: data.is_shootout ?? prev.isShootout,
            periodInProgress: !data.ended_at,
            lastEvent: 'score.updated',
            lastEventAt: Date.now(),
          }))
        }
        setLastScoreUpdate(Date.now())
      })

      // Listen for timeline events
      channel.current.bind('goal.scored', (data) => {
        const event = wsGoalToTimelineEvent(data)
        setWsTimelineEvents((prev) => ({
          ...prev,
          goals: { ...prev.goals, [data.goal_id]: event },
        }))
        if (data.home_team_score !== undefined) {
          setWsHomeScore(data.home_team_score)
        }
        if (data.visiting_team_score !== undefined) {
          setWsVisitingScore(data.visiting_team_score)
        }
        setLastScoreUpdate(Date.now())
        // Update game state with period/clock info from goal event
        if (data.period_clock_time || data.period_number) {
          setWsGameState((prev) => ({
            ...prev,
            periodNumber: data.period_number ?? prev.periodNumber,
            periodName: data.period_name ?? prev.periodName,
            clockTime: data.period_clock_time ?? prev.clockTime,
            periodInProgress: true,
            lastEvent: 'goal.scored',
            lastEventAt: Date.now(),
          }))
        }
      })

      channel.current.bind('goal.deleted', (data) => {
        setWsTimelineEvents((prev) => {
          const { [data.goal_id]: removed, ...remainingGoals } = prev.goals
          return {
            ...prev,
            goals: remainingGoals,
            deletedIds: new Set([...prev.deletedIds, data.goal_id]),
          }
        })
        if (data.home_team_score !== undefined) {
          setWsHomeScore(data.home_team_score)
        }
        if (data.visiting_team_score !== undefined) {
          setWsVisitingScore(data.visiting_team_score)
        }
        setLastScoreUpdate(Date.now())
      })

      channel.current.bind('shot.created', (data) => {
        const event = wsShotToTimelineEvent(data)
        setWsTimelineEvents((prev) => ({
          ...prev,
          shots: { ...prev.shots, [data.shot_id]: event },
        }))
        // Update game state with period/clock info from shot event
        if (data.period_clock_time || data.period_number) {
          setWsGameState((prev) => ({
            ...prev,
            periodNumber: data.period_number ?? prev.periodNumber,
            periodName: data.period_name ?? prev.periodName,
            clockTime: data.period_clock_time ?? prev.clockTime,
            periodInProgress: true,
            lastEvent: 'shot.created',
            lastEventAt: Date.now(),
          }))
        }
      })

      channel.current.bind('shot.deleted', (data) => {
        setWsTimelineEvents((prev) => {
          const { [data.shot_id]: removed, ...remainingShots } = prev.shots
          return {
            ...prev,
            shots: remainingShots,
            deletedIds: new Set([...prev.deletedIds, data.shot_id]),
          }
        })
      })

      channel.current.bind('penalty.created', (data) => {
        const event = wsPenaltyToTimelineEvent(data)
        setWsTimelineEvents((prev) => ({
          ...prev,
          penalties: { ...prev.penalties, [data.offense_id]: event },
        }))
        // Update game state with period/clock info from penalty event
        if (data.period_clock_time || data.period_number) {
          setWsGameState((prev) => ({
            ...prev,
            periodNumber: data.period_number ?? prev.periodNumber,
            periodName: data.period_name ?? prev.periodName,
            clockTime: data.period_clock_time ?? prev.clockTime,
            periodInProgress: true,
            lastEvent: 'penalty.created',
            lastEventAt: Date.now(),
          }))
        }
      })

      channel.current.bind('penalty.deleted', (data) => {
        setWsTimelineEvents((prev) => {
          const { [data.offense_id]: removed, ...remainingPenalties } =
            prev.penalties
          return {
            ...prev,
            penalties: remainingPenalties,
            deletedIds: new Set([...prev.deletedIds, data.offense_id]),
          }
        })
      })

      channel.current.bind('goalie.changed', (data) => {
        const event = wsGoalieChangeToTimelineEvent(data)
        setWsTimelineEvents((prev) => ({
          ...prev,
          goalieChanges: {
            ...prev.goalieChanges,
            [data.substitution_id]: event,
          },
        }))
        // Update game state with period/clock info from goalie change event
        if (data.period_clock_time || data.period_number) {
          setWsGameState((prev) => ({
            ...prev,
            periodNumber: data.period_number ?? prev.periodNumber,
            periodName: data.period_name ?? prev.periodName,
            clockTime: data.period_clock_time ?? prev.clockTime,
            periodInProgress: true,
            lastEvent: 'goalie.changed',
            lastEventAt: Date.now(),
          }))
        }
      })

      channel.current.bind('period.started', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          periodNumber: data.period_number ?? prev.periodNumber,
          periodName: data.period_name ?? prev.periodName,
          clockTime: data.clock_time ?? prev.clockTime,
          isOvertime: data.is_overtime ?? prev.isOvertime,
          isShootout: data.is_shootout ?? prev.isShootout,
          periodInProgress: true,
          lastEvent: 'period.started',
          lastEventAt: Date.now(),
        }))
      })

      channel.current.bind('period.ended', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          periodNumber: data.period_number ?? prev.periodNumber,
          periodName: data.period_name ?? prev.periodName,
          clockTime: data.clock_time ?? prev.clockTime,
          periodInProgress: false,
          lastEvent: 'period.ended',
          lastEventAt: Date.now(),
        }))
        if (data.home_team_score !== undefined) {
          setWsHomeScore(data.home_team_score)
        }
        if (data.visiting_team_score !== undefined) {
          setWsVisitingScore(data.visiting_team_score)
        }
      })

      // game.finalized handler
      channel.current.bind('game.finalized', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          isGameFinalized: true,
          gameStatusId: data.game_status_id ?? prev.gameStatusId,
          periodInProgress: false,
          lastEvent: 'game.finalized',
          lastEventAt: Date.now(),
        }))
        if (data.home_team_score !== undefined) {
          setWsHomeScore(data.home_team_score)
        }
        if (data.visiting_team_score !== undefined) {
          setWsVisitingScore(data.visiting_team_score)
        }
        setLastScoreUpdate(Date.now())
      })

      // period.created handler
      // Only update display info if the period has started (started_at is set)
      // Otherwise, keep showing the previous period's state until period.started is received
      channel.current.bind('period.created', (data) => {
        // Only update period display if period has actually started
        if (data.started_at) {
          setWsGameState((prev) => ({
            ...prev,
            periodNumber: data.period_number ?? prev.periodNumber,
            periodName: data.period_name ?? prev.periodName,
            clockTime: data.clock_time ?? prev.clockTime,
            isOvertime: data.is_overtime ?? prev.isOvertime,
            periodInProgress: true,
            lastEvent: 'period.created',
            lastEventAt: Date.now(),
          }))
        }
        // If started_at is null, period was created but not started yet
        // Keep current display state until period.started event arrives
      })

      // period.reopened handler
      channel.current.bind('period.reopened', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          periodNumber: data.period_number ?? prev.periodNumber,
          periodName: data.period_name ?? prev.periodName,
          clockTime: data.clock_time ?? prev.clockTime,
          isOvertime: data.is_overtime ?? prev.isOvertime,
          periodInProgress: true,
          isGameFinalized: false,
          lastEvent: 'period.reopened',
          lastEventAt: Date.now(),
        }))
      })

      // period.updated handler
      channel.current.bind('period.updated', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          periodNumber: data.period_number ?? prev.periodNumber,
          periodName: data.period_name ?? prev.periodName,
          clockTime: data.clock_time ?? prev.clockTime,
          isOvertime: data.is_overtime ?? prev.isOvertime,
          periodInProgress: !data.ended_at,
          lastEvent: 'period.updated',
          lastEventAt: Date.now(),
        }))
      })

      // shootout.started handler
      channel.current.bind('shootout.started', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          isShootout: true,
          shootoutHomeFirst: data.shootout_home_first ?? null,
          periodInProgress: true,
          periodName: 'Shootout',
          lastEvent: 'shootout.started',
          lastEventAt: Date.now(),
        }))
        if (data.home_team_score !== undefined) {
          setWsHomeScore(data.home_team_score)
        }
        if (data.visiting_team_score !== undefined) {
          setWsVisitingScore(data.visiting_team_score)
        }
        setLastScoreUpdate(Date.now())
      })

      // shootout.ended handler
      channel.current.bind('shootout.ended', (data) => {
        setWsGameState((prev) => ({
          ...prev,
          isShootout: false,
          shootoutWinner: data.winner ?? null,
          periodInProgress: false,
          lastEvent: 'shootout.ended',
          lastEventAt: Date.now(),
        }))
        if (data.home_team_score !== undefined) {
          setWsHomeScore(data.home_team_score)
        }
        if (data.visiting_team_score !== undefined) {
          setWsVisitingScore(data.visiting_team_score)
        }
        setLastScoreUpdate(Date.now())
      })
      // Subscribe to reactions channel for cheers
      reactionsChannel.current = client.subscribe(reactionsChannelName)

      reactionsChannel.current.bind('cheer', (data) => {
        setCheerEvents((prev) => [
          ...prev,
          {
            id: `${Date.now()}-${Math.random()}`,
            emoji: data.emoji || ':clap:',
            team: data.team || 'home',
            receivedAt: Date.now(),
          },
        ])
      })
    } catch (err) {
      console.error(
        '[useGameLivestream] Error subscribing to game livestream channel:',
        err
      )
      setError('Error connecting to live updates')
    }

    return () => {
      if (channel.current) {
        channel.current.unbind('livestream.status')
        channel.current.unbind('score.updated')
        channel.current.unbind('goal.scored')
        channel.current.unbind('goal.deleted')
        channel.current.unbind('shot.created')
        channel.current.unbind('shot.deleted')
        channel.current.unbind('penalty.created')
        channel.current.unbind('penalty.deleted')
        channel.current.unbind('goalie.changed')
        channel.current.unbind('period.started')
        channel.current.unbind('period.ended')
        channel.current.unbind('game.finalized')
        channel.current.unbind('period.created')
        channel.current.unbind('period.reopened')
        channel.current.unbind('period.updated')
        channel.current.unbind('shootout.started')
        channel.current.unbind('shootout.ended')
        channel.current.unbind('pusher:subscription_succeeded')
        channel.current.unbind('pusher:subscription_error')

        client.unsubscribe(channelName)
        channel.current = null
      }

      if (reactionsChannel.current) {
        reactionsChannel.current.unbind('cheer')
        client.unsubscribe(reactionsChannelName)
        reactionsChannel.current = null
      }
    }
  }, [client, isReady, gameId])

  // Reset state when gameId changes to null
  useEffect(() => {
    if (!gameId) {
      setStreams({
        league: createInitialStreamState('league'),
        home: createInitialStreamState('home'),
        visitor: createInitialStreamState('visitor'),
      })
      setSelectedStreamType(null)
      setConnectionState('disconnected')
      setGameInfo({
        homeTeamName: null,
        visitingTeamName: null,
        scheduleName: null,
      })
      setWsHomeScore(null)
      setWsVisitingScore(null)
      setLastScoreUpdate(null)
      setWsTimelineEvents(createInitialWsTimelineState())
      setWsGameState(createInitialWsGameState())
      setCheerEvents([])
      wasLiveRefs.current = { league: false, home: false, visitor: false }
    }
  }, [gameId])

  // Reset WS state function (call after API refresh)
  const resetWsState = useCallback(() => {
    setWsTimelineEvents(createInitialWsTimelineState())
    setWsGameState(createInitialWsGameState())
    setWsHomeScore(null)
    setWsVisitingScore(null)
  }, [])

  // Reset only WS timeline events (preserves scores and game state)
  const resetWsTimelineOnly = useCallback(() => {
    setWsTimelineEvents(createInitialWsTimelineState())
  }, [])

  // Clear a single cheer event after its animation completes
  const clearCheerEvent = useCallback((id) => {
    setCheerEvents((prev) => prev.filter((e) => e.id !== id))
  }, [])

  // Process initial livestream states from game.livestreams
  useEffect(() => {
    if (!Array.isArray(initialLivestreams) || initialLivestreams.length === 0) {
      return
    }

    initialLivestreams.forEach((livestream) => {
      const streamType = livestream.type || 'league'
      if (!VALID_STREAM_TYPES.includes(streamType)) {
        return
      }

      const isActive =
        livestream.status === 'active' || livestream.status === 'recording'

      if (isActive) {
        wasLiveRefs.current[streamType] = true
      }

      // Detect recorded stream (VOD)
      const hasRecording = !!(
        livestream.video_status === 'vod' && livestream.stream_url
      )

      const streamState = mapStatusToBroadcastState(
        livestream.status,
        wasLiveRefs.current[streamType],
        livestream.broadcast_id || livestream.playback_id,
        livestream.stream_url || livestream.playback_url
      )

      setStreams((prev) => ({
        ...prev,
        [streamType]: {
          type: streamType,
          livestreamId: livestream.id || null,
          broadcastState: streamState,
          streamUrl: livestream.stream_url || livestream.playback_url || null,
          broadcastId:
            livestream.broadcast_id || livestream.playback_id || null,
          isLive: isActive,
          wasLive: wasLiveRefs.current[streamType] || isActive,
          hasRecording,
          recordingUrl: hasRecording ? livestream.stream_url : null,
        },
      }))
    })
  }, [initialLivestreams])

  // Auto-select first available stream (live or recorded) when none selected
  useEffect(() => {
    if (!selectedStreamType && availableStreamCount > 0) {
      setSelectedStreamType(availableStreams[0].type)
    }
  }, [selectedStreamType, availableStreamCount, availableStreams])

  // Clear selection if selected stream is no longer available (not live AND no recording)
  useEffect(() => {
    if (selectedStreamType && streams[selectedStreamType]) {
      const stream = streams[selectedStreamType]
      if (!stream.isLive && !stream.hasRecording) {
        setSelectedStreamType(null)
      }
    }
  }, [selectedStreamType, streams])

  /**
   * Update broadcast state from API polling data
   * Called when polling returns new data as a fallback to WebSocket
   * Supports both single stream (legacy) and multi-stream data
   */
  const updateFromApi = useCallback((apiData) => {
    // Handle array of livestreams (multi-stream)
    if (Array.isArray(apiData)) {
      apiData.forEach((livestream) => {
        const streamType = livestream.type || 'league'
        if (!VALID_STREAM_TYPES.includes(streamType)) {
          return
        }
        //video_status: 'live' | 'vod' | 'upcoming' | null
        const isActive =
          livestream.status === 'active' || livestream.status === 'recording'

        if (isActive) {
          wasLiveRefs.current[streamType] = true
        }

        // Detect recorded stream (VOD) - only update if API provides this info
        const hasRecordingFromApi =
          livestream.video_status === 'vod' && !!livestream.stream_url

        const streamState = mapStatusToBroadcastState(
          livestream.status,
          wasLiveRefs.current[streamType],
          livestream.broadcast_id || livestream.playback_id,
          livestream.stream_url || livestream.playback_url
        )

        setStreams((prev) => {
          const existingStream = prev[streamType]
          // Preserve existing recording data if API doesn't provide it
          const hasRecording =
            hasRecordingFromApi || existingStream?.hasRecording || false
          const recordingUrl = hasRecordingFromApi
            ? livestream.stream_url
            : existingStream?.recordingUrl || null

          return {
            ...prev,
            [streamType]: {
              type: streamType,
              livestreamId: livestream.id || null,
              broadcastState: streamState,
              streamUrl:
                livestream.stream_url || livestream.playback_url || null,
              broadcastId:
                livestream.broadcast_id || livestream.playback_id || null,
              isLive: isActive,
              wasLive: wasLiveRefs.current[streamType] || isActive,
              hasRecording,
              recordingUrl,
            },
          }
        })
      })
      return
    }
    //video_status: 'live' | 'vod' | 'upcoming' | null
    // Handle single stream data (legacy format)
    const streamType = apiData?.livestream_type || 'league'
    const validStreamType = VALID_STREAM_TYPES.includes(streamType)
      ? streamType
      : 'league'
    // Consider stream live if is_broadcasting is true OR status is 'active'
    const newIsLive =
      apiData?.is_broadcasting ||
      apiData?.status === 'active' ||
      apiData?.status === 'recording'
    const newStreamUrl = apiData?.playback_url || apiData?.stream_url || null
    const newBroadcastId = apiData?.broadcast_id || null

    // Detect recorded stream (VOD) - only update if API provides this info
    const hasRecordingFromApi =
      apiData?.video_status === 'vod' && !!apiData?.stream_url

    // Track if stream was ever live
    if (newIsLive) {
      wasLiveRefs.current[validStreamType] = true
    }

    const newState = mapStatusToBroadcastState(
      apiData?.status,
      wasLiveRefs.current[validStreamType],
      newBroadcastId,
      newStreamUrl
    )

    setStreams((prev) => {
      const existingStream = prev[validStreamType]
      // Preserve existing recording data if API doesn't provide it
      const hasRecording =
        hasRecordingFromApi || existingStream?.hasRecording || false
      const recordingUrl = hasRecordingFromApi
        ? apiData?.stream_url
        : existingStream?.recordingUrl || null

      return {
        ...prev,
        [validStreamType]: {
          type: validStreamType,
          livestreamId: apiData?.livestream_id || null,
          broadcastState: newState,
          streamUrl: newStreamUrl,
          broadcastId: newBroadcastId,
          isLive: newIsLive,
          wasLive: wasLiveRefs.current[validStreamType] || newIsLive,
          hasRecording,
          recordingUrl,
        },
      }
    })

    // Update gameInfo if available from API
    if (
      apiData?.home_team_name ||
      apiData?.visiting_team_name ||
      apiData?.schedule_name
    ) {
      setGameInfo({
        homeTeamName: apiData.home_team_name || null,
        visitingTeamName: apiData.visiting_team_name || null,
        scheduleName: apiData.schedule_name || null,
      })
    }
  }, [])

  return {
    // Multi-stream state
    streams,
    activeStreams,
    activeStreamCount,
    availableStreams,
    availableStreamCount,
    selectedStreamType,
    setSelectedStreamType,
    selectedStream,

    // Legacy compatibility (single stream values)
    isLive,
    streamUrl,
    broadcastId,
    broadcastState,

    // Connection and error state
    connectionState,
    error,
    updateFromApi,

    // Game info
    gameInfo,

    // WebSocket scores
    wsHomeScore,
    wsVisitingScore,
    lastScoreUpdate,

    // WebSocket timeline events
    wsTimelineEvents,
    wsGameState,
    resetWsState,
    resetWsTimelineOnly,

    // Cheers
    cheerEvents,
    clearCheerEvent,
  }
}

export default useGameLivestream
